import type { FormField } from "@/lib/database/forms";
import { resolveConfirmationTemplateForSubmission, type FormEmailTemplates } from "@/lib/forms/email-templates";
import { templateToSendBulkEmailFields } from "@/lib/forms/merge-email-branding";
import { extractSubmittedEmail } from "@/lib/forms/submitted-email";

/**
 * Resolves and sends a form's confirmation email to the respondent, if one is
 * configured and an email address can be resolved from the submission. Never
 * throws — failures are logged (prefixed with `source`) rather than surfaced,
 * since a confirmation-email failure shouldn't block a successful submission.
 *
 * Shared by every form submit surface (standalone form page, landing-page
 * modal, checkout modal) so the resolution order and failure handling can't
 * drift between them.
 *
 * @param params.formId - The form being confirmed.
 * @param params.eventId - The event the form belongs to.
 * @param params.formData - The cleaned (visible-fields-only) submission payload.
 * @param params.formFields - The form's field definitions, used to resolve the submitter's email.
 * @param params.emailTemplates - The form's custom email templates, if any.
 * @param params.source - Short label identifying the caller, used to prefix log lines.
 * @param params.responseId - The response this submission just created, when known. Threaded
 *   through so the send route can mint the edit link's signed receipt for THIS exact response
 *   instead of re-deriving it from email — two responses can share an address (a host submitting
 *   on behalf of two different people), and an email-keyed lookup can't tell them apart.
 * @returns A promise that resolves once the send attempt (success or failure) completes.
 */
export async function sendFormConfirmationEmail(params: {
  formId: number;
  eventId: number;
  formData: Record<string, unknown>;
  formFields: FormField[];
  emailTemplates: FormEmailTemplates | null | undefined;
  source: string;
  responseId?: number | null;
}): Promise<void> {
  const { formId, eventId, formData, formFields, emailTemplates, source, responseId } = params;

  try {
    // Resolve the SAME confirmation variant the server would pick for this submission — the
    // attending copy, or the "not attending" variant when the RSVP answer declines (see the
    // form's `confirmation_branch`). The plain confirmation must not be hardcoded here: on the
    // manager-send path, send-bulk-email trusts this caller-supplied subject/body instead of
    // re-resolving, so a manager/organizer who answers "No" would otherwise receive the wrong
    // (attending) email. Resolving the branch client-side keeps both send paths in agreement.
    const template = resolveConfirmationTemplateForSubmission(emailTemplates, formData);
    // Match the resolution order used to gate the confirmation send server-side
    // (sendToField → canonical email field → common labels → any "email"-labeled
    // field) so this doesn't silently skip forms that route confirmations to a
    // non-default field. Uses the resolved variant's sendToField so the recipient
    // matches the field the server pins the send to.
    const userEmail = extractSubmittedEmail(formData, formFields, template.sendToField);
    if (!userEmail) return;

    const response = await fetch("/api/forms/send-bulk-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        formId,
        eventId,
        emails: [userEmail],
        // Declare this as a confirmation so the route classifies it as one even when the submitter
        // happens to own/manage the form (e.g. the owner testing their own form) — without this it
        // is treated as a manager blast and routed to the wrong template. It stays pinned to the
        // single submitted address, so it can't become a relay.
        kind: "confirmation",
        ...templateToSendBulkEmailFields(template),
        formData,
        responseId: responseId ?? null,
      }),
    });

    if (!response.ok) {
      console.error(`${source}: confirmation email request failed`, response.status);
      return;
    }

    const result = await response.json().catch(() => null);
    if (result?.failureCount > 0) {
      console.error(`${source}: confirmation email failed to send`, result);
    }
  } catch (emailError) {
    console.error(`${source}: error sending confirmation email`, emailError);
  }
}
